import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Clock, Calendar, User, CheckCircle, LogIn, LogOut, CalendarDays, FileText, Users, Umbrella, Coffee, ArrowLeft, ChevronLeft, ChevronRight, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addDays, isSameDay, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmployeePlanning from '../components/EmployeePlanning';
import SimplePunchClock from '../components/SimplePunchClock';
import { formatCurrency, safeFixed, toNumber } from '../utils/formatters';

const DAYS_OF_WEEK = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

// Self Clock Component for staff - Now using SimplePunchClock
const SelfClockView = ({ user }) => {
  const [myData, setMyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanning, setShowPlanning] = useState(false);

  const employeeId = user?.employee_id;

  useEffect(() => {
    if (employeeId) {
      fetchMyData();
    } else {
      setLoading(false);
    }
  }, [employeeId]);

  const fetchMyData = async () => {
    try {
      const hrDataRes = await api.get('/employees/me');
      setMyData(hrDataRes.data);
    } catch (error) {
      console.error('Error fetching my data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!employeeId) {
    return (
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="py-8 text-center">
          <p className="text-orange-800">Votre compte n'est pas lié à un profil employé. Contactez l'administrateur.</p>
        </CardContent>
      </Card>
    );
  }

  // Show planning view
  if (showPlanning) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setShowPlanning(false)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au pointage
        </Button>
        <EmployeePlanning employeeId={employeeId} isOwner={false} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="self-clock-view">
      {/* Balances Card */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <Umbrella className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{safeFixed(myData?.balances?.leave, 1)}</p>
                <p className="text-xs text-muted-foreground">Jours de congés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{safeFixed(myData?.balances?.recovery, 1)}h</p>
                <p className="text-xs text-muted-foreground">Récupération</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-indigo-100">
                <Coffee className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{myData?.balances?.restDaysOwed || 0}</p>
                <p className="text-xs text-muted-foreground">Repos à prendre</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Simple Punch Clock */}
      <SimplePunchClock 
        employeeId={employeeId} 
        onViewPlanning={() => setShowPlanning(true)}
      />
    </div>
  );
};

// Global Planning Calendar Component - shows all employees' leaves and rest days
const GlobalPlanningCalendar = ({ employees }) => {
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const res = await api.get('/employees/calendar/leaves', {
        params: {
          start: format(start, 'yyyy-MM-dd'),
          end: format(end, 'yyyy-MM-dd')
        }
      });
      setCalendarData(res.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = [];
    let current = start;
    while (current <= end) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  };

  const getEventsForDay = (date, empId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarData.filter(ev => {
      if (ev.employee_id !== empId) return false;
      return dateStr >= ev.start && dateStr <= ev.end;
    });
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'CP': return 'bg-green-500';
      case 'RTT': return 'bg-purple-500';
      case 'REPOS': return 'bg-indigo-500';
      case 'MALADIE': return 'bg-red-500';
      case 'SANS_SOLDE': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  const days = getDaysInMonth();

  return (
    <Card data-testid="global-planning-calendar">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Planning global - {format(currentMonth, 'MMMM yyyy', { locale: fr })}
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
            >
              Aujourd'hui
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white z-10 p-2 border text-left min-w-[150px]">Employé</th>
                  {days.map((day) => (
                    <th 
                      key={day.toString()} 
                      className={`p-1 border text-center min-w-[32px] ${
                        isSameDay(day, new Date()) ? 'bg-primary/10' :
                        [0, 6].includes(day.getDay()) ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="text-xs text-muted-foreground">{format(day, 'EEE', { locale: fr })}</div>
                      <div className={isSameDay(day, new Date()) ? 'font-bold text-primary' : ''}>{format(day, 'd')}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id}>
                    <td className="sticky left-0 bg-white z-10 p-2 border font-medium">
                      {emp.first_name} {(emp.last_name || '').charAt(0)}.
                    </td>
                    {days.map((day) => {
                      const events = getEventsForDay(day, emp.id);
                      const isWeekend = [0, 6].includes(day.getDay());
                      return (
                        <td 
                          key={day.toString()} 
                          className={`p-0 border ${isWeekend ? 'bg-gray-50' : ''}`}
                        >
                          {events.length > 0 && (
                            <div 
                              className={`h-6 ${getEventColor(events[0].type)}`}
                              title={`${events[0].type}: ${emp.first_name} ${emp.last_name}`}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-sm">Congés payés</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-indigo-500"></div>
            <span className="text-sm">Repos</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-purple-500"></div>
            <span className="text-sm">RTT</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-sm">Maladie</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-500"></div>
            <span className="text-sm">Sans solde</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Full HR Management View
const HRManagementView = ({ can }) => {
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [hrOverview, setHrOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedEmployeeForPlanning, setSelectedEmployeeForPlanning] = useState(null);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [correctionData, setCorrectionData] = useState({ clock_in: '', clock_out: '', notes: '' });

  const [employeeForm, setEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    position: '',
    phone: '',
    email: '',
    hire_date: '',
    monthly_salary: '',
    contract_type: 'FULL_TIME',
    hours_per_day: 8,
    rest_days_per_week: 1,
    leave_days_per_month: 1.5
  });

  useEffect(() => {
    fetchEmployees();
    fetchArchivedEmployees();
    fetchHROverview();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees', { params: { active: true } });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedEmployees = async () => {
    try {
      const response = await api.get('/employees', { params: { active: false } });
      setArchivedEmployees(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des archivés:', error);
    }
  };

  const handleArchiveEmployee = async (employee, archive = true) => {
    const action = archive ? 'archiver' : 'réactiver';
    if (!window.confirm(`${archive ? 'Archiver' : 'Réactiver'} ${employee.first_name} ${employee.last_name} ?`)) return;
    
    try {
      await api.put(`/employees/${employee.id}`, { active: !archive });
      toast.success(`Collaborateur ${action === 'archiver' ? 'archivé' : 'réactivé'}`);
      fetchEmployees();
      fetchArchivedEmployees();
    } catch (error) {
      toast.error(`Erreur lors de l'${action}`);
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Supprimer définitivement ${employee.first_name} ${employee.last_name} ? Cette action est irréversible.`)) return;
    
    try {
      await api.delete(`/employees/${employee.id}`);
      toast.success('Collaborateur supprimé');
      fetchArchivedEmployees();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const fetchHROverview = async () => {
    try {
      const response = await api.get('/employees/hr-overview');
      setHrOverview(response.data);
    } catch (error) {
      console.error('Error fetching HR overview:', error);
    }
  };

  const fetchPendingLeaves = async () => {
    // This would need a new endpoint, for now skip
  };

  const fetchTimeEntries = async (employeeId) => {
    try {
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
      const response = await api.get(`/employees/${employeeId}/time-entries`, {
        params: { from, to }
      });
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch time entries');
    }
  };

  const fetchLeaveRequests = async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/leave-requests`);
      setLeaveRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch leave requests');
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEmployee) {
        await api.put(`/employees/${selectedEmployee.id}`, employeeForm);
        toast.success('Employé mis à jour');
      } else {
        await api.post('/employees', employeeForm);
        toast.success('Employé créé');
      }
      setShowEmployeeDialog(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleClockIn = async (employeeId) => {
    try {
      await api.post(`/employees/${employeeId}/clock-in`);
      toast.success('Pointage arrivée enregistré');
      if (selectedEmployee) {
        fetchTimeEntries(selectedEmployee.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleClockOut = async (employeeId) => {
    try {
      await api.post(`/employees/${employeeId}/clock-out`);
      toast.success('Pointage départ enregistré');
      if (selectedEmployee) {
        fetchTimeEntries(selectedEmployee.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      first_name: '',
      last_name: '',
      position: '',
      phone: '',
      email: '',
      hire_date: '',
      monthly_salary: '',
      contract_type: 'FULL_TIME'
    });
    setSelectedEmployee(null);
  };

  const openEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeForm({
      first_name: employee.first_name,
      last_name: employee.last_name,
      position: employee.position || '',
      phone: employee.phone || '',
      email: employee.email || '',
      hire_date: employee.hire_date || '',
      monthly_salary: employee.monthly_salary || '',
      contract_type: employee.contract_type,
      hours_per_day: employee.hours_per_day || 8,
      rest_days_per_week: employee.rest_days_per_week || 1,
      leave_days_per_month: employee.leave_days_per_month || 1.5
    });
    setShowEmployeeDialog(true);
  };

  const openTimeTracking = (employee) => {
    setSelectedEmployee(employee);
    fetchTimeEntries(employee.id);
    setShowTimeDialog(true);
  };

  const handleCorrectEntry = async () => {
    if (!editingEntry || !selectedEmployee) return;
    try {
      await api.put(`/employees/${selectedEmployee.id}/time-entries/${editingEntry.id}`, correctionData);
      toast.success('Pointage corrigé');
      setShowCorrectionDialog(false);
      setEditingEntry(null);
      fetchTimeEntries(selectedEmployee.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la correction');
    }
  };

  // If viewing an employee's planning
  if (selectedEmployeeForPlanning) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setSelectedEmployeeForPlanning(null)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à la vue RH
        </Button>
        <EmployeePlanning 
          employeeId={selectedEmployeeForPlanning.id} 
          employeeName={`${selectedEmployeeForPlanning.first_name} ${selectedEmployeeForPlanning.last_name}`}
          isOwner={can('hr.manage')} 
          onClose={() => setSelectedEmployeeForPlanning(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-management-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Ressources Humaines</h1>
          <p className="text-muted-foreground mt-1">Gestion du personnel</p>
        </div>
        {can('hr.manage') && (
          <Button onClick={() => { resetEmployeeForm(); setShowEmployeeDialog(true); }} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel employé
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Users className="w-4 h-4 mr-2" />
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">
            <User className="w-4 h-4 mr-2" />
            Employés
          </TabsTrigger>
          <TabsTrigger value="planning" data-testid="tab-planning">
            <Calendar className="w-4 h-4 mr-2" />
            Planning & Congés
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Summary Cards */}
          {hrOverview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary">{hrOverview?.summary?.total ?? 0}</p>
                      <p className="text-sm text-muted-foreground">Effectif total</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-green-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{hrOverview?.summary?.present ?? 0}</p>
                      <p className="text-sm text-muted-foreground">Présents aujourd'hui</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{hrOverview?.summary?.onLeave ?? 0}</p>
                      <p className="text-sm text-muted-foreground">En congé</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-orange-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-orange-600">{hrOverview?.summary?.notClocked ?? 0}</p>
                      <p className="text-sm text-muted-foreground">Non pointés</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Weekly/Monthly Hours Summary */}
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    Compteurs d'heures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Weekly Hours */}
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-3 flex items-center gap-1">
                        <CalendarDays className="w-4 h-4" />
                        Cette semaine
                      </h4>
                      <div className="space-y-2">
                        {(hrOverview?.employees || []).map((emp) => (
                          <div key={emp.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                            <span className="font-medium text-sm">{(emp.name || 'Employé').split(' ')[0]}</span>
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${(emp.weekHours || 0) > 45 ? 'text-red-600' : 'text-indigo-600'}`}>
                                {emp.weekHours || 0}h
                              </span>
                              {(emp.weekHours || 0) > 45 && (
                                <Badge variant="destructive" className="text-xs">+{((emp.weekHours || 0) - 45).toFixed(0)}h</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="flex items-center justify-between p-2 bg-indigo-100 rounded-lg font-semibold">
                          <span>Total équipe</span>
                          <span className="text-indigo-700">
                            {(hrOverview?.employees?.reduce((sum, e) => sum + (e.weekHours || 0), 0) || 0).toFixed(1)}h
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Hours - Using actual data from API */}
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-3 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Ce mois ({format(new Date(), 'MMMM', { locale: fr })})
                      </h4>
                      <div className="space-y-2">
                        {(hrOverview?.employees || []).map((emp) => (
                          <div key={emp.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                            <span className="font-medium text-sm">{(emp.name || 'Employé').split(' ')[0]}</span>
                            <span className="font-bold text-purple-600">{emp.monthHours || 0}h</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between p-2 bg-purple-100 rounded-lg font-semibold">
                          <span>Total équipe</span>
                          <span className="text-purple-700">
                            {(hrOverview?.employees?.reduce((sum, e) => sum + (e.monthHours || 0), 0) || 0).toFixed(1)}h
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Quick Actions for Today */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pointage du jour - {format(new Date(), 'EEEE d MMMM', { locale: fr })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {employees.map((employee) => (
                  <div key={employee.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                    <span className="font-medium">{employee.first_name}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleClockIn(employee.id)}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      data-testid={`clock-in-${employee.id}`}
                    >
                      <LogIn className="w-3 h-3 mr-1" />
                      Arrivée
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleClockOut(employee.id)}
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      data-testid={`clock-out-${employee.id}`}
                    >
                      <LogOut className="w-3 h-3 mr-1" />
                      Départ
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Employee Status Table */}
          {hrOverview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Situation du personnel</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Employé</th>
                      <th className="text-left p-2">Poste</th>
                      <th className="text-center p-2">H. semaine</th>
                      <th className="text-center p-2">H. mois</th>
                      <th className="text-center p-2">Congés</th>
                      <th className="text-center p-2">Récup</th>
                      <th className="text-center p-2">Repos dus</th>
                      <th className="text-center p-2">Statut</th>
                      <th className="text-right p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(hrOverview?.employees || []).map((emp) => (
                      <tr key={emp.id} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{emp.name || 'Sans nom'}</td>
                        <td className="p-2 text-muted-foreground">{emp.position || '-'}</td>
                        <td className="p-2 text-center">
                          <span className={(emp.weekHours || 0) > 45 ? 'text-red-600 font-bold' : ''}>
                            {emp.weekHours || 0}h
                          </span>
                          {(emp.weekHours || 0) > 45 && (
                            <Badge variant="destructive" className="ml-1 text-xs">+{((emp.weekHours || 0) - 45).toFixed(0)}h</Badge>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <span className="font-medium text-purple-600">{emp.monthHours || 0}h</span>
                        </td>
                        <td className="p-2 text-center">
                          <Badge variant="outline" className="bg-green-50">{safeFixed(emp.balances?.leave, 1)}j</Badge>
                        </td>
                        <td className="p-2 text-center">
                          <Badge variant="outline" className="bg-amber-50">{safeFixed(emp.balances?.recovery, 1)}h</Badge>
                        </td>
                        <td className="p-2 text-center">
                          {(emp.balances?.restDaysOwed ?? 0) > 0 ? (
                            <Badge variant="outline" className="bg-indigo-50">{emp.balances?.restDaysOwed}</Badge>
                          ) : '-'}
                        </td>
                        <td className="p-2 text-center">
                          <Badge className={
                            emp.todayStatus === 'PRESENT' ? 'bg-green-100 text-green-800' :
                            emp.todayStatus === 'LEAVE' || emp.todayStatus === 'REST' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {emp.todayStatus === 'PRESENT' && 'Présent'}
                            {emp.todayStatus === 'NOT_CLOCKED' && 'Non pointé'}
                            {emp.todayStatus === 'LEAVE' && 'Congé'}
                            {emp.todayStatus === 'REST' && 'Repos'}
                          </Badge>
                        </td>
                        <td className="p-2 text-right">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedEmployeeForPlanning(employees.find(e => e.id === emp.id))}
                            data-testid={`view-planning-${emp.id}`}
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-6">
          {/* Toggle actifs/archivés */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant={!showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(false)}
              >
                <Users className="w-4 h-4 mr-2" />
                Actifs ({employees.length})
              </Button>
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(true)}
              >
                <Archive className="w-4 h-4 mr-2" />
                Archivés ({archivedEmployees.length})
              </Button>
            </div>
          </div>

          {/* Employees Grid */}
          {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (showArchived ? archivedEmployees : employees).length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            {showArchived ? 'Aucun employé archivé' : 'Aucun employé trouvé'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(showArchived ? archivedEmployees : employees).map((employee) => (
            <Card key={employee.id} className={`card-metric ${showArchived ? 'opacity-75' : ''}`} data-testid={`employee-card-${employee.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${showArchived ? 'bg-gray-100' : 'bg-primary/10'}`}>
                      <User className={`w-6 h-6 ${showArchived ? 'text-gray-400' : 'text-primary'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{employee.first_name} {employee.last_name}</h3>
                      <p className="text-sm text-muted-foreground">{employee.position || 'Non défini'}</p>
                      {showArchived && (
                        <Badge variant="outline" className="mt-1 bg-gray-100 text-gray-600">Archivé</Badge>
                      )}
                    </div>
                  </div>
                  {can('hr.edit') && !showArchived && (
                    <Button variant="ghost" size="icon" onClick={() => openEditEmployee(employee)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {employee.phone && (
                    <p className="text-muted-foreground">📞 {employee.phone}</p>
                  )}
                  {employee.monthly_salary && can('hr.manage') && (
                    <p>
                      <span className="text-muted-foreground">Salaire:</span>{' '}
                      <span className="font-semibold">{formatCurrency(employee.monthly_salary)}/mois</span>
                    </p>
                  )}
                  <p>
                    <Badge variant="outline">
                      {employee.contract_type === 'FULL_TIME' && 'Temps plein'}
                      {employee.contract_type === 'PART_TIME' && 'Temps partiel'}
                      {employee.contract_type === 'CONTRACT' && 'Contrat'}
                    </Badge>
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t flex gap-2">
                  {showArchived ? (
                    /* Actions pour employés archivés */
                    <>
                      {can('hr.manage') && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleArchiveEmployee(employee, false)}
                          >
                            <ArchiveRestore className="w-4 h-4 mr-1" />
                            Réactiver
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDeleteEmployee(employee)}
                            title="Supprimer définitivement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    /* Actions pour employés actifs */
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openTimeTracking(employee)}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Pointages
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedEmployeeForPlanning(employee)}
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      {can('hr.manage') && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          onClick={() => handleArchiveEmployee(employee, true)}
                          title="Archiver"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        {/* Planning Tab */}
        <TabsContent value="planning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Planning et gestion des congés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">Sélectionnez un employé pour voir et gérer son planning.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {employees.map((employee) => (
                  <Button
                    key={employee.id}
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center justify-center gap-2"
                    onClick={() => setSelectedEmployeeForPlanning(employee)}
                    data-testid={`planning-select-${employee.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                      <p className="text-xs text-muted-foreground">{employee.position}</p>
                    </div>
                    <div className="flex gap-1">
                      <Badge variant="outline" className="text-xs bg-green-50">{safeFixed(employee.leave_balance, 1)}j</Badge>
                      <Badge variant="outline" className="text-xs bg-amber-50">{safeFixed(employee.recovery_balance, 1)}h</Badge>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Global Planning Calendar */}
          <GlobalPlanningCalendar employees={employees} />
        </TabsContent>
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmployeeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })}
                  required
                  data-testid="input-emp-first-name"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })}
                  required
                  data-testid="input-emp-last-name"
                />
              </div>
              <div>
                <Label>Poste</Label>
                <Input
                  value={employeeForm.position}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                  placeholder="Gouvernante, cuisinier..."
                />
              </div>
              <div>
                <Label>Type de contrat</Label>
                <Select value={employeeForm.contract_type} onValueChange={(v) => setEmployeeForm({ ...employeeForm, contract_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FULL_TIME">Temps plein</SelectItem>
                    <SelectItem value="PART_TIME">Temps partiel</SelectItem>
                    <SelectItem value="CONTRACT">Contrat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={employeeForm.phone}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Date d'embauche</Label>
                <Input
                  type="date"
                  value={employeeForm.hire_date}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, hire_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Salaire mensuel</Label>
                <Input
                  type="number"
                  value={employeeForm.monthly_salary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, monthly_salary: e.target.value })}
                />
              </div>
            </div>
            
            {/* Configuration RH */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Configuration RH</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Heures / jour</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="1"
                    max="12"
                    value={employeeForm.hours_per_day || 8}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, hours_per_day: parseFloat(e.target.value) })}
                    data-testid="input-hours-per-day"
                  />
                </div>
                <div>
                  <Label className="text-xs">Repos / semaine</Label>
                  <Input
                    type="number"
                    min="1"
                    max="3"
                    value={employeeForm.rest_days_per_week || 1}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, rest_days_per_week: parseInt(e.target.value) })}
                    data-testid="input-rest-days-week"
                  />
                </div>
                <div>
                  <Label className="text-xs">Congés / mois</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="5"
                    value={employeeForm.leave_days_per_month || 1.5}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, leave_days_per_month: parseFloat(e.target.value) })}
                    data-testid="input-leave-days-month"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEmployeeDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-employee">
                {selectedEmployee ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Tracking Dialog */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Pointages - {selectedEmployee?.first_name} {selectedEmployee?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={() => handleClockIn(selectedEmployee?.id)} className="flex-1 bg-green-600 hover:bg-green-700">
                <LogIn className="w-4 h-4 mr-2" />
                Pointer arrivée
              </Button>
              <Button variant="outline" onClick={() => handleClockOut(selectedEmployee?.id)} className="flex-1">
                <LogOut className="w-4 h-4 mr-2" />
                Pointer départ
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Arrivée</th>
                    <th className="text-left p-2">Départ</th>
                    <th className="text-left p-2">Heures</th>
                    <th className="text-left p-2">Statut</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry) => (
                    <tr key={entry.id} className={`border-t ${entry.corrected ? 'bg-amber-50' : ''}`}>
                      <td className="p-2">{format(new Date(entry.entry_date), 'dd/MM/yyyy')}</td>
                      <td className="p-2 text-green-600 font-medium">
                        {entry.clock_in || '-'}
                        {entry.corrected && entry.original_clock_in && (
                          <span className="text-xs text-muted-foreground ml-1">(était {entry.original_clock_in})</span>
                        )}
                      </td>
                      <td className="p-2 text-orange-600 font-medium">
                        {entry.clock_out || '-'}
                        {entry.corrected && entry.original_clock_out && (
                          <span className="text-xs text-muted-foreground ml-1">(était {entry.original_clock_out})</span>
                        )}
                      </td>
                      <td className="p-2">{entry.total_hours ? `${entry.total_hours}h` : '-'}</td>
                      <td className="p-2">
                        <Badge className={
                          entry.status === 'PRESENT' ? 'bg-green-100 text-green-800' :
                          entry.status === 'ABSENT' ? 'bg-red-100 text-red-800' :
                          entry.status === 'LEAVE' ? 'bg-blue-100 text-blue-800' : 
                          'bg-gray-100 text-gray-800'
                        }>
                          {entry.status === 'PRESENT' && 'Présent'}
                          {entry.status === 'ABSENT' && 'Absent'}
                          {entry.status === 'LATE' && 'Retard'}
                          {entry.status === 'LEAVE' && 'Congé'}
                          {entry.status === 'REST' && 'Repos'}
                        </Badge>
                        {entry.corrected && (
                          <Badge variant="outline" className="ml-1 text-xs bg-amber-100 text-amber-800">Corrigé</Badge>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingEntry(entry);
                            setCorrectionData({
                              clock_in: entry.clock_in || '',
                              clock_out: entry.clock_out || '',
                              notes: entry.notes || ''
                            });
                            setShowCorrectionDialog(true);
                          }}
                          title="Corriger le pointage"
                          data-testid={`correct-entry-${entry.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {timeEntries.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center p-4 text-muted-foreground">
                        Aucun pointage ce mois-ci
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Corriger le pointage</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p className="font-medium">{format(new Date(editingEntry.entry_date), 'EEEE dd MMMM yyyy', { locale: fr })}</p>
                <p className="text-muted-foreground">Employé: {selectedEmployee?.first_name} {selectedEmployee?.last_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heure d'arrivée</Label>
                  <Input
                    type="time"
                    value={correctionData.clock_in}
                    onChange={(e) => setCorrectionData({ ...correctionData, clock_in: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Heure de départ</Label>
                  <Input
                    type="time"
                    value={correctionData.clock_out}
                    onChange={(e) => setCorrectionData({ ...correctionData, clock_out: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Notes (raison de la correction)</Label>
                <Input
                  value={correctionData.notes}
                  onChange={(e) => setCorrectionData({ ...correctionData, notes: e.target.value })}
                  placeholder="Ex: Oubli de pointage, erreur système..."
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCorrectionDialog(false)}>Annuler</Button>
                <Button onClick={handleCorrectEntry}>Enregistrer la correction</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function EmployeesPage() {
  const { can, user } = useAuth();
  
  // Staff (role without hr.manage) sees only self-clock view
  // Manager/Owner with hr.manage see full management view
  const hasManagePermission = can('hr.manage');
  
  if (!hasManagePermission && user?.employee_id) {
    return <SelfClockView user={user} />;
  }
  
  return <HRManagementView can={can} />;
}
// Build timestamp: 1774899708
